import { Alert } from '@creditornot/cb-alert';
import { FormRow } from '@creditornot/cb-form';
import { cbSemanticCssVars, typographyCss } from '@creditornot/cb-ingredients';
import { Tag } from '@creditornot/cb-tag';
import { DateTime } from 'luxon';
import React, { useContext, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled, { useTheme } from 'styled-components';

import { ItemConfiguration } from 'apis/picker-api/types';
import { ProductBrand } from 'apis/product-information-catalog/types';
import {
  Category,
  Item,
  ItemCommission,
  MenuConfigs,
  ProductHierarchyLevel,
  ProductInformationSettings,
  Translation,
} from 'apis/restaurant-api/merchant-admin/menu2/types';
import MonetaryAmount from 'common/formatting/MonetaryAmount';
import { useDeposits, useLocaleUtils, usePluralization } from 'common/hooks';
import { Typography as T } from 'common/typography';
import { isDefined, localize } from 'common/utils';
import { useCountryConfigContext } from 'modules/country/CountryConfigContext';
import { dietaryPreferenceTranslationMap, WOLT_SPECIALS_CATEGORY_ID } from 'modules/menu/constants';
import { useIsMinQuantityFieldEnabled } from 'modules/menu/hooks/useIsMinQuantityFieldEnabled';
import { ItemQuantity } from 'modules/menu/items/types';
import { mandatoryWarningTranslationMap } from 'modules/menu/mandatoryWarnings';

import FormValueContainer from './FormValueContainer';
import ImageView from './ImageView';
import ItemRestrictions from './ItemRestrictions';
import { MenuLocaleContext } from '../../../../MenuLocaleContext';
import { MenuFeaturesContext } from '../../../../components/MenuFeaturesContext';
import { MenuPermissionsContext } from '../../../../components/MenuPermissionsContext';
import InventoryMngmtDetailsView from '../InventoryMngmtDetailsView';
import CommissionsView from '../ItemCommissions/ItemCommissionsView';
import OptionBindingsView from '../OptionBindingsView';
import WeeklyScheduleView from '../WeeklyScheduleView';

type Props = {
  category: Category | null;
  parentCategory: Category | null;
  className?: string;
  item: Item;
  productInfoSettings: ProductInformationSettings | undefined;
  pickingAreaItemAttrs: { pickingAreaName: string; pickingItemSequence: number | undefined } | undefined;
  itemConfig: ItemConfiguration | undefined;
  itemQuantity: ItemQuantity | undefined;
  commissions: ItemCommission[] | undefined;
  brand: ProductBrand | null;
  returnPolicies: MenuConfigs['item_return_policies'];
};

const deliveryMethods: { [key in DeliveryMethod]: string } = {
  eatin: 'delivery-types.eatin',
  homedelivery: 'delivery-types.homedelivery',
  takeaway: 'delivery-types.takeaway',
};

const Root = styled.div`
  position: relative;
  user-select: text;
`;

const ItemPosAlert = styled(Alert).attrs({
  size: 'medium',
  variant: 'info',
})`
  margin-block-start: 24px;
  max-width: 612px;
`;

const Heading = styled.div`
  ${typographyCss.Title1};
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 0;
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
`;

const Section = styled.div`
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
  & + & {
    margin-block-start: 24px;
  }
`;

const SectionTitle = styled.div`
  ${typographyCss.Title1};
  padding-block-end: 24px;
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
`;

const ConnectedTag = styled(Tag)`
  margin-inline-start: 16px;
`;

const VatInfo = styled(T.Small)`
  color: ${cbSemanticCssVars.textTertiary};
  margin-inline-start: 8px;
`;

const StyledFormValueContainer = styled(FormValueContainer)`
  & + & {
    margin-block-start: 16px;
  }
`;

const ProductInfoValue = styled.div`
  flex: 1 1 0; /* Ensure long lines don't mess up layout */
  white-space: pre-wrap; /* Product info values can have line breaks, so render them correctly */
`;

const NutritionValuesContainer = styled.div`
  max-width: 392px;
`;

const NutritionBaseline = styled.div`
  border-block-end: 1px solid ${cbSemanticCssVars.textPrimary};
  padding-block-end: 12px;
`;

const NutritionValueRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
  & + & {
    border-block-start: 1px solid ${cbSemanticCssVars.borderPrimary};
  }
`;

const StorageLocationSection = styled.div`
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
`;

const StorageLocationHeader = styled.div`
  ${typographyCss.Title1};
  padding: 16px 0;
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
`;

const StorageLocationInfo = styled.div`
  margin-block-start: 8px;
  ${typographyCss.Body3};
  font-weight: normal;
  white-space: normal;
`;

const nutritionBaselineTranslationKeys = {
  per_100g: 'menu-item.nutrition-baseline-100g',
  per_100ml: 'menu-item.nutrition-baseline-100ml',
};

const caffeineBaselineTranslationKeys: Record<NonNullable<Item['caffeine_content']>['units'], string> = {
  mg_per_100_g: 'menu-item.caffeine-baseline-100g',
  mg_per_100_ml: 'menu-item.caffeine-baseline-100ml',
};

const Row = styled.div`
  margin-block-start: 12px;
`;

const getHierarchyLabelPath = (
  hierarchyLevels: ProductHierarchyLevel[],
  id: string,
  path: Translation[][] = [],
): Translation[][] => {
  const currentLevel = hierarchyLevels.find(level => level.id === id);
  if (!currentLevel) {
    return path;
  }
  const currentPath = [currentLevel.name, ...path];
  return !currentLevel.parent_category_id
    ? currentPath
    : getHierarchyLabelPath(hierarchyLevels, currentLevel.parent_category_id, currentPath);
};

const Availability = ({ start, end }: { start: DateTime | null; end: DateTime | null }) => {
  return (
    <T.Body3>
      {start && !end && <FormattedMessage id="special.availability-starting-label" />}
      {!start && end && <FormattedMessage id="special.availability-ending-label" />}
      {start && <span>{start.toFormat('d.M.y HH:mm')}</span>}
      {start && end && <span> - </span>}
      {end && <span>{end.toFormat('d.M.y HH:mm')}</span>}
      {!start && !end && <FormattedMessage id="time-of-week.always" />}
    </T.Body3>
  );
};

const ItemDetailsView = ({
  category,
  parentCategory,
  className,
  item,
  productInfoSettings,
  pickingAreaItemAttrs,
  itemQuantity,
  commissions,
  itemConfig,
  brand,
  returnPolicies,
}: Props) => {
  const { alcohol_warning_permille_limit: alcoholWarningPermilleLimit } = useCountryConfigContext();
  const intl = useIntl();
  const theme = useTheme();
  const { formatPluralMessage } = usePluralization();
  const { getLocalizedUnit, priceFormatterPerUnit } = useLocaleUtils();

  const isMinQuantityFieldEnabled = useIsMinQuantityFieldEnabled();

  const menuFeatures = useContext(MenuFeaturesContext);
  const menuPermissions = useContext(MenuPermissionsContext);
  const { country, primaryLanguage, timezone } = useContext(MenuLocaleContext);

  const { data: depositOptions } = useDeposits(country, menuFeatures.bottleDeposits);
  const deposit = depositOptions.find(option => option.value === item.deposit_permutation_id?.$oid);

  const categoryName = useMemo(() => {
    const categoryPath = [parentCategory, category]
      .map(category => (category ? localize(category?.name, primaryLanguage) : undefined))
      .filter(isDefined);

    return {
      lang: category
        ? category.name.find(name => name.lang === primaryLanguage)?.lang || category.name[0].lang
        : undefined,
      value: theme.cbDirection === 'ltr' ? categoryPath.join(' › ') : [...categoryPath].reverse().join(' ‹ '),
    };
  }, [category, parentCategory, primaryLanguage, theme.cbDirection]);

  const itemName = item.name.find(name => name.lang === primaryLanguage) || item.name[0];
  const itemDescription = item.description.find(desc => desc.lang === primaryLanguage) || item.description[0];
  const hasStorageLocationEnabled = menuFeatures.hasPickerEnabled && menuFeatures.isRetail;

  const returnPolicyLabel = useMemo(() => {
    if (!item.return_policy || !returnPolicies?.length) {
      return '-';
    }

    const foundReturnPolicy = returnPolicies.find(({ type }) => item.return_policy === type);

    if (!foundReturnPolicy) {
      return item.return_policy;
    }

    return `${foundReturnPolicy.description} - ${formatPluralMessage({
      id: 'menu-item.return-policy-days',
      count: foundReturnPolicy.days,
    })}`;
  }, [formatPluralMessage, item.return_policy, returnPolicies]);

  const formattedNutritionValues = useMemo(() => {
    if (!item.nutrition_values || !productInfoSettings?.nutrition_values) {
      return [];
    }
    return item.nutrition_values
      .map(value => {
        const nutritionSetting = productInfoSettings.nutrition_values.find(opt => opt.id === value.nutrition_value_id);
        return {
          label: localize(nutritionSetting?.name || [], intl.locale),
          amount: `${value.amount} ${localize(nutritionSetting?.unit || [], intl.locale)}`,
          position: nutritionSetting?.table_row_number || 9999,
        };
      })
      .sort((a, b) => a.position - b.position);
  }, [intl.locale, item.nutrition_values, productInfoSettings?.nutrition_values]);

  const productHierarchyLabel = useMemo(() => {
    if (!productInfoSettings?.product_hierarchy_levels || !item.product_hierarchy_level_id) {
      return '';
    }
    const labelPath = getHierarchyLabelPath(
      productInfoSettings?.product_hierarchy_levels,
      item.product_hierarchy_level_id,
    ).map(label => localize(label, intl.locale));
    return theme.cbDirection === 'ltr' ? labelPath.join(' › ') : [...labelPath].reverse().join(' ‹ ');
  }, [intl.locale, item.product_hierarchy_level_id, productInfoSettings?.product_hierarchy_levels, theme.cbDirection]);

  return (
    <Root dir={theme.cbDirection} className={className}>
      {!menuPermissions.contentEdit && menuFeatures.posIntegration && (
        <ItemPosAlert title={intl.formatMessage({ id: 'merchant-integrations.pos-integrated-venue-header' })}>
          <FormattedMessage id="merchant-integrations.pos-integrated-venue-item-message" />
        </ItemPosAlert>
      )}

      <Heading>
        <FormattedMessage id="menu-item.details-heading" />
      </Heading>

      <Section>
        {menuFeatures.hasCatalogSource && (
          <>
            <FormRow label={intl.formatMessage({ id: 'menu.barcode-gtin' })}>
              {item.barcode_gtin || '-'}
              {!!item.source_config && (
                <ConnectedTag variant="lightBlack" size="small">
                  Connected
                </ConnectedTag>
              )}
            </FormRow>
            <FormRow label={intl.formatMessage({ id: 'menu.merchant-sku' })}>{item.merchant_sku || '-'}</FormRow>
          </>
        )}
        <FormRow label={intl.formatMessage({ id: 'menu-item.category' })}>
          <T.Body3 dir="auto" lang={categoryName?.lang}>
            {categoryName?.value || '-'}
          </T.Body3>
        </FormRow>
        <FormRow label={intl.formatMessage({ id: 'menu-item.name' })}>
          <T.Body3 dir="auto" lang={itemName.lang}>
            {itemName.value}
          </T.Body3>
        </FormRow>
        <FormRow label={intl.formatMessage({ id: 'menu-item.product-family' })}>{productHierarchyLabel}</FormRow>
        <FormRow label={intl.formatMessage({ id: 'menu-item.brand' })}>
          {!!brand ? localize(brand?.name.translations, brand?.name.primaryLanguage) : '-'}
        </FormRow>
        <FormRow label={intl.formatMessage({ id: 'menu-item.description' })}>
          <T.Body3 dir="auto" lang={itemDescription?.lang}>
            {itemDescription?.value || '-'}
          </T.Body3>
        </FormRow>
        <FormRow label={intl.formatMessage({ id: 'menu-item.price' })}>
          <MonetaryAmount amount={parseFloat(item.baseprice.amount)} currency={item.baseprice.currency} />
          {!!item.sell_by_weight_config && `/${getLocalizedUnit('kilogram')}`}
          <VatInfo>
            <FormattedMessage
              id="menu-item.vat-label"
              values={{
                percentage: (Number(item.vat_percentage) / 100).toLocaleString(undefined, {
                  style: 'percent',
                  maximumFractionDigits: 2,
                }),
              }}
            />
          </VatInfo>
        </FormRow>
        {item.discounted && (
          <FormRow label={intl.formatMessage({ id: 'special.original-price' })}>
            <MonetaryAmount
              amount={parseFloat(item.discounted.original_price.amount)}
              currency={item.discounted.original_price.currency}
            />
            {!!item.sell_by_weight_config && `/${getLocalizedUnit('kilogram')}`}
          </FormRow>
        )}
        <FormRow label={intl.formatMessage({ id: 'menu-item.unit-details' })}>
          {!!item.sell_by_weight_config && (
            <StyledFormValueContainer
              label={
                item.sell_by_weight_config.input_type === 'grams'
                  ? intl.formatMessage({ id: 'menu-item.grams-per-step.minimum-weight-steps' })
                  : intl.formatMessage({ id: 'menu-item.grams-per-step.approximate-weight' })
              }
              hint={priceFormatterPerUnit(
                Number(item.baseprice.amount) * (item.sell_by_weight_config.grams_per_step / 1000),
                item.baseprice.currency,
                {
                  baseUnit: item.display_unit === 'weight_100g' ? '100_gram' : 'kilogram',
                  weightInGrams: item.sell_by_weight_config.grams_per_step,
                  isSoldByWeight: item.sell_by_weight_config.input_type === 'grams',
                },
              )}
            >
              {item.sell_by_weight_config.grams_per_step}
              {getLocalizedUnit('gram')}
            </StyledFormValueContainer>
          )}

          {item.weight_in_grams && (
            <StyledFormValueContainer
              label={intl.formatMessage({ id: 'menu-item.weight' })}
              hint={
                item.display_unit === 'weight' || item.display_unit === 'weight_100g'
                  ? priceFormatterPerUnit(Number(item.baseprice.amount), item.baseprice.currency, {
                      baseUnit: item.display_unit === 'weight_100g' ? '100_gram' : 'kilogram',
                      weightInGrams: item.weight_in_grams,
                    })
                  : undefined
              }
            >
              {item.weight_in_grams} {getLocalizedUnit('gram')}
            </StyledFormValueContainer>
          )}

          {item.volume_in_ml && (
            <StyledFormValueContainer
              label={intl.formatMessage({ id: 'menu-item.volume' })}
              hint={
                item.display_unit === 'volume'
                  ? priceFormatterPerUnit(Number(item.baseprice.amount), item.baseprice.currency, {
                      baseUnit: 'liter',
                      volumeInMl: item.volume_in_ml,
                    })
                  : undefined
              }
            >
              {item.volume_in_ml} {getLocalizedUnit('milliliter')}
            </StyledFormValueContainer>
          )}

          {item.number_of_units && isMinQuantityFieldEnabled && (
            <StyledFormValueContainer
              label={intl.formatMessage({ id: 'menu-item.quantity' })}
              hint={
                item.display_unit === 'units'
                  ? priceFormatterPerUnit(Number(item.baseprice.amount), item.baseprice.currency, {
                      baseUnit: 'pc',
                      numberOfUnits: item.number_of_units,
                    })
                  : undefined
              }
            >
              {item.number_of_units} <FormattedMessage id="menu-item.display-unit-pcs" />
            </StyledFormValueContainer>
          )}
        </FormRow>
        <FormRow label={intl.formatMessage({ id: 'menu-item.alcoholPercentage' })}>
          {
            // alcohol_percentage is in permilles
            (item.alcohol_percentage / 1000).toLocaleString(undefined, {
              style: 'percent',
              // Precision greater than 1 permille is not allowed, so only render one fraction digit
              minimumFractionDigits: 1,
              maximumFractionDigits: 1,
            })
          }
        </FormRow>
        <FormRow label={intl.formatMessage({ id: 'menu-item.caffeine' })}>
          {item.caffeine_content?.value && item.caffeine_content?.units
            ? `${item.caffeine_content.value}${intl.formatMessage({
                id: 'menu-item.caffeine-baseline-unit',
              })} ${intl.formatMessage({ id: caffeineBaselineTranslationKeys[item.caffeine_content.units] })}`
            : '-'}
        </FormRow>
        {menuPermissions.systemIdRead && <FormRow label={intl.formatMessage({ id: 'menu.id' })}>{item.id}</FormRow>}
        {menuPermissions.posIdField && (
          <FormRow label={intl.formatMessage({ id: 'menu.pos-id' })}>{item.external_id || '-'}</FormRow>
        )}
        {/* Don't show these fields here if feed integration is enabled, because they are rendered at the top in that case. */}
        {menuFeatures.isRetail && !menuFeatures.hasCatalogSource && (
          <FormRow label={intl.formatMessage({ id: 'menu.barcode-gtin' })}>{item.barcode_gtin || '-'}</FormRow>
        )}
        {menuFeatures.isRetail && !menuFeatures.hasCatalogSource && (
          <FormRow label={intl.formatMessage({ id: 'menu.merchant-sku' })}>{item.merchant_sku || '-'}</FormRow>
        )}
        <FormRow label={intl.formatMessage({ id: 'menu-item.image' })}>
          <ImageView item={item} />
        </FormRow>
      </Section>
      <Section>
        <SectionTitle>
          <FormattedMessage id="menu-item.advanced-details" />
        </SectionTitle>

        <FormRow label={intl.formatMessage({ id: 'menu.dietaryPreferences.title' })}>
          {item.dietary_preferences && item.dietary_preferences.length > 0 ? (
            <div style={{ margin: '-4px 0' }}>
              {item.dietary_preferences.map(type => (
                <Tag key={type} variant="lightBlue" size="small" style={{ margin: '4px 8px 4px 0' }}>
                  <FormattedMessage id={dietaryPreferenceTranslationMap[type]} />
                </Tag>
              ))}
            </div>
          ) : (
            '-'
          )}
        </FormRow>

        <FormRow label={intl.formatMessage({ id: 'menu.country-of-origin' })}>
          <ProductInfoValue>{localize(item.country_of_origin, primaryLanguage, true) || '-'}</ProductInfoValue>
        </FormRow>

        <FormRow label={intl.formatMessage({ id: 'menu.ingredients' })}>
          <ProductInfoValue>{localize(item.ingredients, primaryLanguage, true) || '-'}</ProductInfoValue>
        </FormRow>

        <FormRow label={intl.formatMessage({ id: 'menu.additives' })}>
          <ProductInfoValue>{localize(item.additives, primaryLanguage, true) || '-'}</ProductInfoValue>
        </FormRow>

        <FormRow label={intl.formatMessage({ id: 'menu.allergens' })}>
          <ProductInfoValue>{localize(item.allergens, primaryLanguage, true) || '-'}</ProductInfoValue>
        </FormRow>

        {item.nutrition_values.length === 0 && (
          <FormRow label={intl.formatMessage({ id: 'menu.nutrition-facts' })}>
            <ProductInfoValue>{localize(item.nutrition_facts, primaryLanguage, true) || '-'}</ProductInfoValue>
          </FormRow>
        )}

        {item.nutrition_values.length > 0 && (
          <FormRow label={intl.formatMessage({ id: 'menu.nutrition-facts' })}>
            <NutritionValuesContainer>
              <NutritionBaseline>
                {item.nutrition_values_baseline
                  ? intl.formatMessage({ id: nutritionBaselineTranslationKeys[item.nutrition_values_baseline] })
                  : '-'}
              </NutritionBaseline>
              <ProductInfoValue>
                {formattedNutritionValues.map(value => (
                  <NutritionValueRow key={`${value.label}-${value.amount}`}>
                    <div>{value.label}</div>
                    <div>{value.amount}</div>
                  </NutritionValueRow>
                ))}
              </ProductInfoValue>
            </NutritionValuesContainer>
          </FormRow>
        )}

        <FormRow label={intl.formatMessage({ id: 'menu.usage-instructions' })}>
          <ProductInfoValue>
            {localize(item.conditions_of_use_and_storage, primaryLanguage, true) || '-'}
          </ProductInfoValue>
        </FormRow>

        <FormRow label={intl.formatMessage({ id: 'menu.more-information' })}>
          <ProductInfoValue>{localize(item.more_information, primaryLanguage, true) || '-'}</ProductInfoValue>
        </FormRow>

        <FormRow label={intl.formatMessage({ id: 'menu.producer-information' })}>
          <ProductInfoValue>{localize(item.producer_information, primaryLanguage, true) || '-'}</ProductInfoValue>
        </FormRow>

        <FormRow label={intl.formatMessage({ id: 'menu.distributor-information' })}>
          <ProductInfoValue>{localize(item.distributor_information, primaryLanguage, true) || '-'}</ProductInfoValue>
        </FormRow>

        {(menuFeatures.bundleItems || item.is_bundle_offer) && (
          <FormRow label={intl.formatMessage({ id: 'menu-item.bundle-offer' })}>
            <FormattedMessage id={item.is_bundle_offer ? 'menu-item.bundle-true' : 'menu-item.bundle-false'} />
          </FormRow>
        )}

        {menuFeatures.bottleDeposits && (
          <FormRow
            label={intl.formatMessage({ id: 'menu-item.deposit' })}
            info={intl.formatMessage({ id: 'menu-item.deposit-tooltip' })}
          >
            {deposit?.label || '-'}
          </FormRow>
        )}

        {menuFeatures.hasReturnPoliciesEnabled && (
          <FormRow label={intl.formatMessage({ id: 'menu-item.return-policy' })}>{returnPolicyLabel}</FormRow>
        )}

        {menuFeatures.mandatoryWarnings && (
          <FormRow label={intl.formatMessage({ id: 'menu.mandatoryWarnings.title' })}>
            {item.mandatory_warnings?.length > 0 ? (
              <div style={{ margin: '-4px 0' }}>
                {item.mandatory_warnings.map(warning => (
                  <Tag key={warning} variant="lightBlue" size="small" style={{ margin: '4px 8px 4px 0' }}>
                    <FormattedMessage id={mandatoryWarningTranslationMap[warning]} />
                  </Tag>
                ))}
              </div>
            ) : (
              '-'
            )}
          </FormRow>
        )}

        {((menuFeatures.overTheCounter &&
          item.restrictions.some(restriction => restriction.type === 'over_the_counter') &&
          menuFeatures.overTheCounterHyperlinkAllowed) ||
          menuFeatures.externalInfoHyperlinkAllowed) && (
          <>
            <FormRow label={intl.formatMessage({ id: 'menu-item.external-item-link.form-row-label' })}>
              <StyledFormValueContainer
                label={intl.formatMessage({ id: 'menu-item.external-item-link.hyperlink-label' })}
              >
                {item.external_info_hyperlink?.hyperlink || '-'}
              </StyledFormValueContainer>
              <StyledFormValueContainer label={intl.formatMessage({ id: 'menu-item.external-item-link.text-label' })}>
                {localize(item.external_info_hyperlink?.text ?? [], primaryLanguage, true) || '-'}
              </StyledFormValueContainer>
            </FormRow>
          </>
        )}
      </Section>
      <Section>
        <SectionTitle>
          <FormattedMessage id={'menu-item.availability-and-restrictions'} />
        </SectionTitle>

        <FormRow label={intl.formatMessage({ id: 'menu-item.enabled' })}>
          <FormattedMessage id={item.enabled ? 'menu-item.enabled-true' : 'menu-item.enabled-false'} />
        </FormRow>

        <FormRow label={intl.formatMessage({ id: 'special.availability' })}>
          <Availability
            start={
              item.availability_period.start &&
              DateTime.fromMillis(item.availability_period.start.$date).setZone(timezone)
            }
            end={
              item.availability_period.end && DateTime.fromMillis(item.availability_period.end.$date).setZone(timezone)
            }
          />
        </FormRow>

        <FormRow label={intl.formatMessage({ id: 'menu-item.time-available' })}>
          <WeeklyScheduleView weeklySchedule={item.weekly_availability} />
        </FormRow>

        <FormRow label={intl.formatMessage({ id: 'menu-item.time-visible' })}>
          <WeeklyScheduleView weeklySchedule={item.weekly_visibility} />
        </FormRow>

        {category?.id === WOLT_SPECIALS_CATEGORY_ID && (
          <FormRow label={intl.formatMessage({ id: 'menu-item.ftu-restriction-label' })}>
            {item.user_requirements.length > 0 ? (
              <>
                {item.user_requirements.includes('no_wolt_purchases') && (
                  <div>
                    <FormattedMessage id="menu-item.ftu-restriction-values.ftu-wolt" />
                  </div>
                )}
                {item.user_requirements.includes('no_venue_purchases') && (
                  <div>
                    <FormattedMessage id="menu-item.ftu-restriction-values.ftu-venue" />
                  </div>
                )}
                {item.user_requirements.includes('no_franchise_purchases') && (
                  <div>
                    <FormattedMessage id="menu-item.ftu-restriction-values.ftu-franchise" />
                  </div>
                )}
              </>
            ) : (
              <FormattedMessage id="menu-item.ftu-restriction-none" />
            )}
          </FormRow>
        )}

        {category?.id === WOLT_SPECIALS_CATEGORY_ID && menuFeatures.hasWoltPlusSpecialsEnabled && (
          <FormRow label={intl.formatMessage({ id: 'menu-item.wolt-plus-only.label' })}>
            {item.wolt_plus_only ? (
              <FormattedMessage id="menu-item.wolt-plus-only.yes" />
            ) : (
              <FormattedMessage id="menu-item.wolt-plus-only.no" />
            )}
          </FormRow>
        )}

        <FormRow label={intl.formatMessage({ id: 'menu-item.min-quantity-per-purchase' })}>
          {!item.min_quantity_per_purchase
            ? '-'
            : `${item.min_quantity_per_purchase}${
                item.sell_by_weight_config?.input_type === 'grams' ? getLocalizedUnit('gram') : ''
              }`}
        </FormRow>

        <FormRow label={intl.formatMessage({ id: 'menu-item.max-quantity-per-purchase' })}>
          {!item.max_quantity_per_purchase
            ? '-'
            : `${item.max_quantity_per_purchase}${
                item.sell_by_weight_config?.input_type === 'grams' ? getLocalizedUnit('gram') : ''
              }`}
        </FormRow>

        <ItemRestrictions
          item={item}
          productHierarchies={productInfoSettings?.product_hierarchy_levels || []}
          alcoholWarningPermilleLimit={alcoholWarningPermilleLimit}
        />

        <FormRow label={intl.formatMessage({ id: 'menu-item.deliveryMethods' })}>
          {item.delivery_methods.map(type => intl.formatMessage({ id: deliveryMethods[type] })).join(', ')}
        </FormRow>

        <FormRow label={intl.formatMessage({ id: 'menu-item.courierRestrictions' })}>
          {item.courier_restrictions.length > 0 ? (
            <>
              {item.courier_restrictions.includes('no_bikes') && (
                <div>
                  <FormattedMessage id="menu-item.disableBikes" />
                </div>
              )}
              {item.courier_restrictions.includes('no_cars') && (
                <div>
                  <FormattedMessage id="menu-item.disableCars" />
                </div>
              )}
              {item.courier_restrictions.includes('no_motorcycles') && (
                <div>
                  <FormattedMessage id="menu-item.disableMotorcycles" />
                </div>
              )}
            </>
          ) : (
            '-'
          )}
        </FormRow>
      </Section>

      {hasStorageLocationEnabled && (
        <StorageLocationSection>
          <StorageLocationHeader>
            <FormattedMessage id="menu-item.picking-route.title" />
            <StorageLocationInfo>
              <FormattedMessage id={'menu-item.picking-route.subtitle'} />
            </StorageLocationInfo>
          </StorageLocationHeader>
          <FormRow label={intl.formatMessage({ id: 'menu-item.picking-route.picking-area' })}>
            {pickingAreaItemAttrs?.pickingAreaName || '-'}
            <Row>
              <FormattedMessage id="menu-item.picking-route.sequence-placeholder" />:{' '}
              {pickingAreaItemAttrs?.pickingItemSequence || '-'}
            </Row>
          </FormRow>
          <FormRow label={intl.formatMessage({ id: 'menu-item.picking-route.location' })}>
            {itemConfig?.storageLocation || '-'}
          </FormRow>
        </StorageLocationSection>
      )}

      <InventoryMngmtDetailsView item={item} itemQuantity={itemQuantity} />

      {menuPermissions.customCommissionRead && commissions && <CommissionsView commissions={commissions} />}

      <OptionBindingsView item={item} isWeightedItem={!!item.sell_by_weight_config} />
    </Root>
  );
};

export default ItemDetailsView;
